import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ServiceProviderAvailabilityService from '../services/ServiceProviderAvailabilityService';
import { RootState } from './store';
import { IServiceProviderAvailability, IServiceProviderAvailabilityRequest } from "@ontime/types";

// --- LocalStorage Helpers ---
const loadState = (): ServiceProviderAvailabilityState => {
    try {
        const serializedState = localStorage.getItem('serviceProviderAvailabilitys');
        return serializedState ? JSON.parse(serializedState) : { serviceProviderAvailabilitys: [], loading: false, error: null };
    } catch (error) {
        console.error('Could not load service providers state:', error);
        return { serviceProviderAvailabilitys: [], loading: false, error: null };
    }
};

const saveState = (state: ServiceProviderAvailabilityState) => {
    try {
        localStorage.setItem('serviceProviderAvailabilitys', JSON.stringify(state));
    } catch (error) {
        console.error('Could not save service providers state:', error);
    }
};

// --- Async Thunks ---
export const fetchServiceProviderAvailabilitys = createAsyncThunk(
    'serviceProviderAvailabilitys/fetchAll',
    async ({ organizationId, serviceProviderId, showDeleted }: { organizationId: string; serviceProviderId: string; showDeleted: boolean; }) => {
        try {
            return await ServiceProviderAvailabilityService.GetList(organizationId, serviceProviderId, showDeleted);
        } catch (error) {
            return null;
        }
    }
);

export const getServiceProviderAvailabilityAsync = createAsyncThunk(
    'serviceProviderAvailabilitys/get',
    async ({ organizationId, serviceProviderId, serviceProviderAvailabilityId }: { organizationId: string; serviceProviderId: string; serviceProviderAvailabilityId: string }) => {
        try {
            return await ServiceProviderAvailabilityService.Get(organizationId, serviceProviderId, serviceProviderAvailabilityId);
        } catch (error) {
            return null;
        }
    }
);

export const createServiceProviderAvailabilityAsync = createAsyncThunk(
    'serviceProviderAvailabilitys/create',
    async ({ organizationId, serviceProviderId, data }: { organizationId: string; serviceProviderId: string; data: IServiceProviderAvailabilityRequest }) => {
        try {
            return await ServiceProviderAvailabilityService.Create(organizationId, serviceProviderId, data);
        } catch (error) {
            return null;
        }
    }
);

export const updateServiceProviderAvailabilityAsync = createAsyncThunk(
    'serviceProviderAvailabilitys/update',
    async ({ organizationId, serviceProviderId, data }: { organizationId: string; serviceProviderId: string; data: IServiceProviderAvailabilityRequest }) => {
        try {
            return await ServiceProviderAvailabilityService.Update(organizationId, serviceProviderId, data);
        } catch (error) {
            return null;
        }
    }
);

export const deleteServiceProviderAvailabilityAsync = createAsyncThunk(
    'serviceProviderAvailabilitys/delete',
    async ({ organizationId, serviceProviderId, serviceProviderAvailabilityId, currentIsDeleted }: { organizationId: string; serviceProviderId: string; serviceProviderAvailabilityId: string, currentIsDeleted: boolean }) => {
        try {
            return await ServiceProviderAvailabilityService.Delete(organizationId, serviceProviderId, serviceProviderAvailabilityId, currentIsDeleted);
        } catch (error) {
            return null;
        }
    }
);

// --- State Interface ---
interface ServiceProviderAvailabilityState {
    serviceProviderAvailabilitys: IServiceProviderAvailability[];
    loading: boolean;
    error: string | null;
}

const initialState: ServiceProviderAvailabilityState = loadState();

// --- Slice ---
const serviceProviderAvailabilitySlice = createSlice({
    name: 'serviceProviderAvailabilitys',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchServiceProviderAvailabilitys.pending, (state) => {
                state.serviceProviderAvailabilitys = [];
                state.loading = true;
                state.error = null;
                saveState(state);
            })
            .addCase(fetchServiceProviderAvailabilitys.fulfilled, (state, action) => {
                state.loading = false;
                state.serviceProviderAvailabilitys = action.payload ?? [];
                saveState(state);
            })
            .addCase(fetchServiceProviderAvailabilitys.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                saveState(state);
            })
            .addCase(createServiceProviderAvailabilityAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.serviceProviderAvailabilitys.unshift(action.payload);
                    saveState(state);
                }
            })
            .addCase(updateServiceProviderAvailabilityAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.serviceProviderAvailabilitys.findIndex(sp => sp.serviceProviderAvailabilityId === action.payload?.serviceProviderAvailabilityId);
                    if (index !== -1) {
                        state.serviceProviderAvailabilitys[index] = action.payload;
                        saveState(state);
                    }
                }
            })
            .addCase(deleteServiceProviderAvailabilityAsync.fulfilled, (state, action) => {
                state.serviceProviderAvailabilitys = state.serviceProviderAvailabilitys.filter(sp => sp.serviceProviderAvailabilityId !== action.payload?.serviceProviderAvailabilityId);
                saveState(state);
            });
    }
});

export default serviceProviderAvailabilitySlice.reducer;
export const selectServiceProviderAvailabilitys = (state: RootState) => state.serviceProviderAvailabilities.serviceProviderAvailabilitys;
export const selectServiceProviderAvailabilitysLoading = (state: RootState) => state.serviceProviderAvailabilities.loading;
export const selectServiceProviderAvailabilitysError = (state: RootState) => state.serviceProviderAvailabilities.error;